import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  breadcrumbSchema,
  faqSchema,
  personSchema,
  serviceSchema,
  webPageSchema,
} from "@/lib/seo";
import { NetworkNextStep } from "@/components/NetworkNextStep";
const speakingPhoto = "/img/speaking-photo.webp";

const LECTERN_SRC = "/landing/speaking-lectern.webp";
const LECTERN_ALT =
  "An empty wooden lectern on a softly lit stage with rows of velvet seats slightly out of focus, lit by a warm gold spotlight.";

export default function Speaking() {
  const SEO_TITLE =
    "Speaking & Keynotes by Shariq Refai, MD, MBA, Psychiatrist";
  const SEO_DESCRIPTION =
    "Book board-certified psychiatrist Shariq Refai, MD, MBA, for keynotes on anxiety, performance pressure, emotional health, and mental health literacy.";

  const topics = [
    {
      title: "The Modern Anxiety Loop",
      desc: "Why high-achievers get stuck in cycles of overthinking, what the nervous system actually does under modern pressure, and what useful tools and practices look like in everyday life. The talk takes the patterns most adults are quietly carrying, names them, and gives the audience tools they can use the same week. Best for general professional audiences and conferences.",
      schemaDesc:
        "Why high-achievers get stuck in cycles of overthinking and what useful tools and practices look like in everyday life.",
    },
    {
      title: "Overthinking and the Nervous System",
      desc: "The body moves first. By the time most people notice their thoughts, their nervous system has already shifted. This talk translates the physiology into plain language and explains why trying to out-think panic almost always makes it worse. Strong fit for healthcare audiences and high-performance teams.",
      schemaDesc:
        "Translating the physiology of anxiety into plain language and explaining why trying to out-think panic almost always makes it worse.",
    },
    {
      title: "Mental Health Literacy in the Workplace",
      desc: "Beyond the buzzwords. What organizations actually need to do if they want functional psychological safety, real mental health benefits, and a culture where high performance and wellbeing aren't pulling in opposite directions. Practical, candid, with the parts most consultants skip.",
      schemaDesc:
        "What organizations actually need to do for functional psychological safety, real mental health benefits, and a culture where performance and wellbeing aren't pulling in opposite directions.",
    },
    {
      title: "Psychiatry, Technology, and Access to Care",
      desc: "How digital health models can responsibly close the gap between demand and psychiatric availability without the corner-cutting that's defined the first wave of telehealth. What good telepsychiatry looks like, what bad telepsychiatry looks like, and what the next decade has to deliver. Built for healthcare audiences, technology audiences, and policy circles.",
      schemaDesc:
        "How digital health models can responsibly close the gap between demand and psychiatric availability, with examples of good and bad telepsychiatry.",
    },
    {
      title: "Performance Pressure and Emotional Health",
      desc: "Insights from working with professional athletes, executives, and other high-stakes performers on maintaining baseline regulation when the pressure is constant. The high-performer pattern is a wiring problem, not a personality, and the wiring can be retrained. Excellent fit for athletic organizations, executive teams, and professional services firms.",
      schemaDesc:
        "Insights from working with professional athletes and executives on maintaining baseline regulation under constant pressure.",
    },
    {
      title: "Digital Mental Health Done Responsibly",
      desc: "The line between consumer wellness tools and clinical interventions is getting blurred in ways that cost real patients real outcomes. This talk draws that line clearly, with examples of what's working in the field, what isn't, and what good builders are doing differently. Strong fit for tech audiences, healthcare leaders, and investors.",
      schemaDesc:
        "Drawing the line between consumer wellness tools and clinical interventions, with examples of what's working in the field and what isn't.",
    },
    {
      title: "What Psychiatry Gets Right (And What People Still Misunderstand)",
      desc: "Demystifying psychiatric care for non-clinical audiences. What the field has actually figured out in the last twenty years, what the public conversation still gets wrong about medication and therapy, and how to find care that fits a real life. Best for general audiences, leadership teams, and journalist or media-facing groups.",
      schemaDesc:
        "Demystifying psychiatric care for non-clinical audiences: what the field has figured out and what the public still gets wrong.",
    },
  ];

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/speaking"
        image="/og-speaking.jpg"
        keywords="psychiatrist keynote speaker, mental health speaker, sports psychiatry speaker, workplace mental health, digital mental health"
        schema={[
          personSchema,
          webPageSchema({
            path: "/speaking",
            title: SEO_TITLE,
            description: SEO_DESCRIPTION,
            breadcrumbs: [
              { name: "Home", path: "/" },
              { name: "Speaking", path: "/speaking" },
            ],
            speakableSelectors: [".quick-answer"],
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Speaking", path: "/speaking" },
          ]),
          ...topics.map((t) =>
            serviceSchema({
              name: t.title,
              description: t.schemaDesc,
              serviceType: "Keynote Speaking",
            }),
          ),
          faqSchema([
            {
              question:
                "How far in advance should I book Dr. Refai for a speaking engagement?",
              answer:
                "Booking lead time depends on event date, format, and customization required. Most engagements are confirmed 2 to 6 months in advance. Inquiries are reviewed within three business days.",
            },
            {
              question: "What audiences does Dr. Refai speak to?",
              answer:
                "Corporate leadership teams, healthcare organizations, professional sports teams, professional services firms, medical societies, conferences, and universities. The most common audience is high-performing adults inside organizations taking mental health seriously.",
            },
            {
              question: "Will Dr. Refai customize the talk for our audience?",
              answer:
                "Yes. Customization is included for most engagements. Topic, length, format, and audience-specific examples are tailored during the planning conversation.",
            },
            {
              question: "Is Dr. Refai available for virtual keynotes?",
              answer:
                "Yes. Virtual keynotes are offered for distributed teams and international audiences. Half-day workshops are also available virtually.",
            },
            {
              question: "Are presentations and workshops clinical?",
              answer:
                "No. Presentations and workshops are strictly educational. They don't include individualized clinical advice or any form of physician-patient relationship.",
            },
            {
              question: "How do I request fees and availability?",
              answer:
                "Use the contact form at shariqrefai.com/contact with 'Speaking Engagement' selected as the inquiry type. Include the audience, date or date range, format, and any topic specifics.",
            },
          ]),
        ]}
      />

      {/* Intro / H1 */}
      <section className="intro pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Speaking" },
            ]}
          />
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="md:col-span-7 order-2 md:order-1">
              {/* Credibility eyebrow */}
              <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
                Speaker · Shariq Refai, MD, MBA, FAPA · Board-Certified Psychiatrist
              </p>
              {/* Outcome-led H1: names the value the room gets */}
              <h1 className="mt-4 text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
                Talks on the anxiety loop, performance pressure, and modern psychiatry.
              </h1>
              {/* Subhead: audience + mechanism */}
              <p className="lede mt-6 text-lg md:text-xl text-secondary font-light leading-relaxed">
                Educational keynotes and workshops for corporate teams, professional associations, athletic organizations, and conferences. Clinical rigor, plain language, and the specific patterns your audience is already carrying into the room.
              </p>
              {/* Primary + secondary CTA */}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  href="/media-inquiries"
                  className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
                >
                  Book Dr. Refai
                </Link>
                <Link
                  href="/media"
                  className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
                >
                  See the press kit
                </Link>
              </div>
              {/* Proof strip */}
              <ul aria-label="Credentials" className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground list-none p-0">
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Board certified (ABPN)</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Sports & performance psychiatry certified</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>NFL & Jacksonville Jaguars affiliations</li>
              </ul>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <figure className="relative m-0">
                <img
                  src={LECTERN_SRC}
                  alt={LECTERN_ALT}
                  width={1200}
                  height={900}
                  className="w-full h-auto object-cover aspect-[4/3] shadow-sm"
                  loading="eager"
                  fetchPriority="high"
                />
              </figure>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 px-4 md:px-8 container mx-auto max-w-6xl">
        <div className="grid md:grid-cols-[1fr_2fr] gap-16 items-start">
          {/* Sidebar */}
          <div className="md:sticky md:top-32">
            <div className="aspect-[3/4] relative overflow-hidden bg-muted">
              <img
                src={speakingPhoto}
                alt="Shariq Refai, MD, MBA, speaking on stage."
                className="w-full h-full object-cover grayscale-[15%]" width={1000} height={1500} loading="lazy" decoding="async"/>
            </div>
            <div className="mt-8">
              <h2 className="font-serif text-xl mb-4 text-primary">
                Inquiries
              </h2>
              <p className="text-sm font-light text-secondary mb-6 leading-relaxed">
                Use the{" "}
                <Link
                  href="/media-inquiries"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                >
                  Media &amp; Press Inquiries page
                </Link>{" "}
                for speaking and media outreach. Presentations and workshops are strictly
                educational and don't include individualized clinical advice.
              </p>
              <Link
                href="/media-inquiries"
                className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-6 text-sm font-medium hover:bg-secondary transition-colors w-full"
              >
                Request Availability
              </Link>
            </div>
          </div>

          {/* Main column */}
          <div className="flex flex-col gap-16">
            {/* The Approach */}
            <section className="approach">
              <h2 className="text-3xl font-serif text-primary mb-6">
                The Approach
              </h2>
              <div className="space-y-5 text-base md:text-lg text-secondary font-light leading-relaxed">
                <p>
                  Most mental health speakers split into two camps. The
                  clinical ones are accurate and unreachable. The motivational
                  ones are reachable and wrong. Dr. Refai's talks sit in a
                  third category. Real psychiatry, translated into language a
                  non-clinical room can actually use, with the disclaimers
                  and the rigor an MD's title is supposed to carry.
                </p>
                <p>
                  The talks are practical, not preachy. The frameworks come
                  from more than fifteen years of experience in psychiatry and mental health care and from the books
                  and essays Dr. Refai writes for the general public. The
                  format adapts to the audience. The content doesn't get
                  watered down.
                </p>
              </div>
            </section>

            {/* Audiences */}
            <section className="audiences">
              <h2 className="text-3xl font-serif text-primary mb-6">
                Audiences
              </h2>
              <p className="text-base md:text-lg text-secondary font-light leading-relaxed">
                Dr. Refai speaks to corporate leadership teams, healthcare
                organizations, professional sports teams and associations,
                professional services firms, conferences and summits,
                professional associations and medical societies, and
                university audiences. The most common audience is
                high-performing adults inside organizations where mental
                health is starting to be taken seriously and the leadership
                is tired of the buzzword version of the conversation.
              </p>
            </section>

            {/* Speaking Topics */}
            <section className="speaking-topics">
              <h2 className="text-3xl font-serif text-primary border-b border-border pb-4 mb-8">
                Speaking Topics
              </h2>
              <div className="space-y-10">
                {topics.map((topic, i) => (
                  <article key={i} className="group">
                    <h3 className="text-xl md:text-2xl font-serif text-primary mb-3 group-hover:text-accent transition-colors">
                      {topic.title}
                    </h3>
                    <p className="text-secondary font-light leading-relaxed">
                      {topic.desc}
                    </p>
                  </article>
                ))}
              </div>
            </section>

            {/* Format Options */}
            <section className="formats">
              <h2 className="text-3xl font-serif text-primary mb-6">
                Format Options
              </h2>
              <p className="text-base md:text-lg text-secondary font-light leading-relaxed">
                Keynotes typically run 30 to 60 minutes with optional Q&amp;A.
                Half-day workshops run 90 to 180 minutes and include
                interactive segments. Fireside chats and panel formats
                available. Virtual keynotes are offered for distributed teams
                and international audiences.
              </p>
            </section>

            {/* Booking */}
            <section className="booking border-t border-border pt-12">
              <h2 className="text-3xl font-serif text-primary mb-6">
                Booking
              </h2>
              <div className="space-y-5 text-base md:text-lg text-secondary font-light leading-relaxed mb-8">
                <p>
                  All speaking engagements are educational presentations. They are not clinical services, do not provide individualized medical advice, and do not establish a physician-patient relationship with any attendee. All bookings are for educational, non-clinical purposes. By booking Dr. Refai for a presentation, the requesting organization acknowledges that the engagement is general education and that no clinical, advisory, or treatment relationship is created with any attendee. Dr. Refai cannot accept individualized clinical questions during or after a presentation; attendees with clinical questions will be referred to their own licensed clinician or to shrinkMD. Each engagement is governed by a separate written speaking-engagement agreement that supersedes any informal communication.
                </p>
                <p>
                  For speaking inquiries, including fees, availability, and
                  customization, please use the{" "}
                  <Link
                    href="/media-inquiries"
                    className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
                  >
                    Media &amp; Press Inquiries page
                  </Link>
                  {" "}with "Speaking Engagement" selected as the inquiry type.
                  Please include the audience, date or date range, format,
                  and any topic specifics. Inquiries are reviewed within
                  three business days.
                </p>
                <p>
                  Note: Presentations and workshops are strictly educational.
                  They don't include individualized clinical advice or any
                  form of physician-patient relationship.
                </p>
              </div>
              <Link
                href="/media-inquiries"
                className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
              >
                Request Availability
              </Link>
            </section>
          </div>
        </div>
      </section>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="speaking_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/media", label: "Press & media resources", why: "Bios, headshots, and quotes for event materials." },
          { href: "/media-inquiries", label: "Media & speaking inquiries form", why: "Structured intake with topic, audience, and timing." },
          { href: "/about", label: "About Shariq Refai, MD, MBA", why: "Full credentials and background for speaker profiles." },
          { href: "/perspectives", label: "Perspectives essays", why: "The written thinking behind the talks." },
        ]}
      />
    </SiteLayout>
  );
}
